import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Row, Spinner } from 'react-bootstrap';
import { HorizontalBar } from 'react-chartjs-2';
import { MdBlock, MdCheckCircle, MdEmail, MdGroup, MdLogin, MdRefresh, MdSecurity, MdTrendingUp } from 'react-icons/md';

import ApiMServer from 'services/ApiMServer';
import {
  chartColors,
  KPI,
  getDashboardCardSurface,
  getDashboardShellStyles,
  getChartTextColor
} from 'constants/dashboardColors';

const EMPTY_USERS_METRICS = {
  generatedAt: null,
  period: {
    last30DaysStart: null,
    lastSixMonthsStart: null
  },
  totals: {
    totalUsers: 0,
    activeUsers: 0,
    blockedUsers: 0,
    emailValidatedUsers: 0,
    emailPendingUsers: 0,
    microsoftLoginUsers: 0,
    masterUsers: 0,
    passwordGenerationUsers: 0,
    sguUsers: 0,
    usersWithFirebaseToken: 0,
    usersWithLastLogin: 0,
    createdLast30Days: 0,
    updatedLast30Days: 0,
    loginsLast30Days: 0
  },
  rates: {
    activeRate: 0,
    blockedRate: 0,
    emailValidationRate: 0,
    microsoftLoginRate: 0,
    sguRate: 0
  },
  distributions: {
    byCountry: [],
    byCompany: [],
    byApp: [],
    bySystem: [],
    byUserType: [],
    registrationsByMonth: []
  }
};

const normalizeUsersMetrics = (data = {}) => ({
  generatedAt: data.generatedAt || null,
  period: {
    ...EMPTY_USERS_METRICS.period,
    ...(data.period || {})
  },
  totals: {
    ...EMPTY_USERS_METRICS.totals,
    ...(data.totals || {})
  },
  rates: {
    ...EMPTY_USERS_METRICS.rates,
    ...(data.rates || {})
  },
  distributions: {
    ...EMPTY_USERS_METRICS.distributions,
    ...(data.distributions || {})
  }
});

const formatNumber = (value) => Number(value || 0).toLocaleString('pt-BR');

const formatPercent = (value) => `${Number(value || 0).toLocaleString('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
})}%`;

const formatGeneratedAt = (value) => {
  if (!value) return 'Sem data de geração';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem data de geração';

  return date.toLocaleString('pt-BR');
};

const getItemLabel = (item, fallback) => {
  return item?.month || item?.country || item?.company || item?.app || item?.system || item?.userType || item?.label || item?.name || item?._id || fallback;
};

const getItemValue = (item) => Number(item?.count ?? item?.total ?? item?.value ?? 0);

const isAutoGeneratedLabel = (label) => /^Item\s+\d+$/i.test(String(label || '').trim());

const toChartSeries = (items = []) => {
  return (Array.isArray(items) ? items : [])
    .map((item, index) => ({
      label: getItemLabel(item, `Item ${index + 1}`),
      value: getItemValue(item)
    }))
    .filter((item) => item.value > 0 && !isAutoGeneratedLabel(item.label));
};

const normalizeLabel = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const findDistributionValue = (items = [], expectedLabel) => {
  const expected = normalizeLabel(expectedLabel);
  const match = (Array.isArray(items) ? items : []).find((item, index) => {
    const label = getItemLabel(item, `Item ${index + 1}`);
    const normalizedLabel = normalizeLabel(label);

    return normalizedLabel === expected || normalizedLabel.includes(expected);
  });

  return match ? getItemValue(match) : null;
};

const createGradient = (ctx, colors, darkMode) => {
  const gradient = ctx.createLinearGradient(0, 0, 300, 0);
  gradient.addColorStop(0, darkMode ? colors.dark : colors.light);
  gradient.addColorStop(1, darkMode ? `${colors.dark}99` : `${colors.light}99`);
  return gradient;
};

const getChartOptions = (darkMode) => ({
  animation: {
    onComplete: function () {
      const chart = this.chart;
      const ctx = chart.ctx;

      ctx.save();
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';

      chart.config.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.controller.getDatasetMeta(datasetIndex);
        const drawnLabels = [];
        const chartRight = chart.chartArea?.right || chart.width;
        const chartLeft = chart.chartArea?.left || 0;

        meta.data.forEach((bar, index) => {
          const value = formatNumber(dataset.data[index]);
          const model = bar._model;
          const barWidth = Math.abs(model.x - model.base);
          const textWidth = ctx.measureText(value).width;
          const fitsInside = barWidth > textWidth + 18;
          const x = fitsInside
            ? Math.min(Math.max(model.x - 10, chartLeft + textWidth + 4), chartRight - 4)
            : Math.min(model.x + textWidth + 10, chartRight - 4);
          const labelBox = {
            x1: x - textWidth - 4,
            x2: x + 4,
            y1: model.y - 7,
            y2: model.y + 7
          };
          const overlaps = drawnLabels.some((box) => (
            labelBox.x1 < box.x2 &&
            labelBox.x2 > box.x1 &&
            labelBox.y1 < box.y2 &&
            labelBox.y2 > box.y1
          ));

          if (overlaps) return;

          ctx.fillStyle = fitsInside ? '#ffffff' : darkMode ? '#f8f9fa' : '#1c4220';
          ctx.fillText(value, x, model.y);
          drawnLabels.push(labelBox);
        });
      });

      ctx.restore();
    }
  },
  scales: {
    yAxes: [{
      ticks: {
        beginAtZero: true,
        fontColor: getChartTextColor(darkMode),
        fontStyle: 'bold',
        precision: 0
      },
      gridLines: {
        display: false
      },
      maxBarThickness: 30
    }],
    xAxes: [{
      ticks: {
        beginAtZero: true,
        fontColor: getChartTextColor(darkMode),
        precision: 0
      },
      gridLines: {
        color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        zeroLineColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
      }
    }]
  },
  legend: {
    display: false
  },
  maintainAspectRatio: false,
  responsive: true,
  tooltips: {
    backgroundColor: darkMode ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
    titleFontColor: darkMode ? '#f8f9fa' : '#333',
    bodyFontColor: darkMode ? '#adb5bd' : '#666',
    borderColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    borderWidth: 1,
    callbacks: {
      label: (item) => `Quantidade: ${item.value}`
    }
  }
});

const UsersMetricCard = ({ icon, title, value, subtitle, accent, darkMode }) => {
  return (
    <Col xs={12} sm={6} xl={3}>
      <div
        className="h-100"
        style={{
          borderRadius: '18px',
          padding: '18px',
          ...getDashboardCardSurface(),
          borderTop: `4px solid ${accent}`
        }}
      >
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <p className="text-uppercase mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.08em', color: darkMode ? '#adb5bd' : '#6c757d' }}>
              {title}
            </p>
            <h2 className="mb-0 fw-bold" style={{ color: darkMode ? '#f8f9fa' : '#1c4220' }}>
              {formatNumber(value)}
            </h2>
          </div>
          <div
            className="d-inline-flex align-items-center justify-content-center"
            style={{
              width: '46px',
              height: '46px',
              borderRadius: '14px',
              background: `${accent}22`,
              color: accent
            }}
          >
            {icon}
          </div>
        </div>
        {subtitle && <small style={{ color: darkMode ? '#adb5bd' : '#6c757d' }}>{subtitle}</small>}
      </div>
    </Col>
  );
};

const RateIndicator = ({ label, value, accent, darkMode }) => {
  const percent = Math.max(0, Math.min(Number(value || 0), 100));

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span style={{ color: darkMode ? '#f8f9fa' : '#1c4220', fontWeight: 600 }}>{label}</span>
        <span style={{ color: darkMode ? '#adb5bd' : '#6c757d' }}>{formatPercent(value)}</span>
      </div>
      <div style={{ height: '9px', background: darkMode ? 'rgba(255, 255, 255, 0.08)' : '#edf2ee', borderRadius: '999px', overflow: 'hidden' }}>
        <div style={{ width: `${percent}%`, height: '100%', background: accent, borderRadius: '999px' }} />
      </div>
    </div>
  );
};

const UsersChartCard = ({ title, items, colorKey, darkMode, lg = 6, height = 300 }) => {
  const series = toChartSeries(items);
  const colors = chartColors[colorKey] || chartColors.green;
  const options = getChartOptions(darkMode);

  return (
    <Col xs={12} lg={lg}>
      <div
        className="h-100"
        style={{
          padding: '18px',
          borderRadius: '18px',
          ...getDashboardCardSurface(),
          borderTop: `4px solid ${darkMode ? colors.dark : colors.light}`
        }}
      >
        <h5 className="fw-bold mb-3" style={{ color: darkMode ? '#f8f9fa' : '#1c4220' }}>{title}</h5>
        <div style={{ height, position: 'relative' }}>
          {series.length ? (
            <HorizontalBar
              data={(canvas) => {
                const ctx = canvas.getContext('2d');

                return {
                  labels: series.map((item) => item.label),
                  datasets: [{
                    label: 'Usuários',
                    data: series.map((item) => item.value),
                    backgroundColor: createGradient(ctx, colors, darkMode),
                    borderColor: darkMode ? colors.dark : colors.light,
                    borderWidth: 1,
                    hoverBackgroundColor: darkMode ? colors.dark : colors.light,
                    hoverBorderColor: darkMode ? '#ffffff' : '#000000'
                  }]
                };
              }}
              options={options}
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              Sem dados para exibir
            </div>
          )}
        </div>
      </div>
    </Col>
  );
};

const UsersDashboard = ({ darkMode, onLoadingChange }) => {
  const [metrics, setMetrics] = useState(EMPTY_USERS_METRICS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (onLoadingChange) onLoadingChange(loading);
  }, [loading, onLoadingChange]);
  const styles = getDashboardShellStyles(darkMode);
  const { totals, rates, distributions } = metrics;
  const sguUsersFromApps = findDistributionValue(distributions.byApp, 'SGU');
  const sguUsers = sguUsersFromApps !== null ? sguUsersFromApps : totals.sguUsers;
  const sguRate = totals.totalUsers ? (sguUsers / totals.totalUsers) * 100 : rates.sguRate;

  const fetchUsersMetrics = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const { data } = await ApiMServer.get('/api/v1/users/metrics');
      setMetrics(normalizeUsersMetrics(data));
    } catch (err) {
      setMetrics(EMPTY_USERS_METRICS);
      setError('Não foi possível carregar as métricas de usuários.');
      console.log('Erro ao buscar métricas de usuários:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsersMetrics();
  }, [fetchUsersMetrics]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5" style={styles.panel}>
        <Spinner animation="border" style={{ color: '#8fad15' }} />
        <span className="ms-2" style={styles.mutedText}>Carregando métricas de usuários...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mb-0">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <span>{error}</span>
          <Button size="sm" variant="outline-danger" onClick={fetchUsersMetrics}>
            <MdRefresh className="me-1" /> Atualizar métricas
          </Button>
        </div>
      </Alert>
    );
  }

  if (!totals.totalUsers) {
    return (
      <div style={{ ...styles.panel, padding: '28px' }}>
        <h4 className="fw-bold mb-2" style={styles.titleText}>Nenhum usuário encontrado</h4>
        <p className="mb-3" style={styles.mutedText}>A API retornou total de usuários igual a zero.</p>
        <Button variant="outline-success" onClick={fetchUsersMetrics}>
          <MdRefresh className="me-1" /> Atualizar métricas
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4" style={{ ...styles.panel, padding: '20px' }}>
        <div>
          <Badge className="mb-2" style={{ background: '#8fad15', color: '#fff' }}>Usuários</Badge>
          <h3 className="fw-bold mb-1" style={styles.titleText}>Dashboard Executivo de Usuários</h3>
          <p className="mb-0" style={styles.mutedText}>
            Métricas agregadas de acesso, status e distribuição dos usuários.
          </p>
        </div>
        <div className="d-flex flex-column align-items-start align-items-md-end gap-2">
          <Badge className="badge-light" pill>Atualizado: {formatGeneratedAt(metrics.generatedAt)}</Badge>
          <Button variant="outline-success" size="sm" onClick={fetchUsersMetrics}>
            <MdRefresh className="me-1" /> Atualizar métricas
          </Button>
        </div>
      </div>

      <Row className="g-3 mb-4">
        <UsersMetricCard icon={<MdGroup size={28} />} title="Total de Usuários" value={totals.totalUsers} subtitle="Base cadastrada" accent={KPI.primary} darkMode={darkMode} />
        <UsersMetricCard icon={<MdCheckCircle size={28} />} title="Usuários Ativos" value={totals.activeUsers} subtitle={formatPercent(rates.activeRate)} accent={KPI.primary} darkMode={darkMode} />
        <UsersMetricCard icon={<MdBlock size={28} />} title="Usuários Bloqueados" value={totals.blockedUsers} subtitle={formatPercent(rates.blockedRate)} accent={KPI.danger} darkMode={darkMode} />
        <UsersMetricCard icon={<MdEmail size={28} />} title="E-mails Validados" value={totals.emailValidatedUsers} subtitle={formatPercent(rates.emailValidationRate)} accent={KPI.secondary} darkMode={darkMode} />
        <UsersMetricCard icon={<MdEmail size={28} />} title="E-mails Pendentes" value={totals.emailPendingUsers} subtitle="Aguardando validação" accent={KPI.warning} darkMode={darkMode} />
        <UsersMetricCard icon={<MdTrendingUp size={28} />} title="Novos em 30 Dias" value={totals.createdLast30Days} subtitle={`${formatNumber(totals.updatedLast30Days)} atualizados`} accent={KPI.tertiary} darkMode={darkMode} />
        <UsersMetricCard icon={<MdLogin size={28} />} title="Logins em 30 Dias" value={totals.loginsLast30Days} subtitle={`${formatNumber(totals.usersWithLastLogin)} com histórico`} accent={KPI.quaternary} darkMode={darkMode} />
        <UsersMetricCard icon={<MdSecurity size={28} />} title="Usuários SGU" value={sguUsers} subtitle={formatPercent(sguRate)} accent={KPI.secondary} darkMode={darkMode} />
      </Row>

      <Row className="g-4 mb-4">
        <Col xs={12} lg={4}>
          <div className="h-100" style={{ ...styles.panel, padding: '18px' }}>
            <h5 className="fw-bold mb-3" style={styles.titleText}>Indicadores Percentuais</h5>
            <RateIndicator label="Usuários ativos" value={rates.activeRate} accent={KPI.primary} darkMode={darkMode} />
            <RateIndicator label="Bloqueados" value={rates.blockedRate} accent={KPI.danger} darkMode={darkMode} />
            <RateIndicator label="Validação de e-mail" value={rates.emailValidationRate} accent={KPI.secondary} darkMode={darkMode} />
            <RateIndicator label="Login Microsoft" value={rates.microsoftLoginRate} accent={KPI.tertiary} darkMode={darkMode} />
            <RateIndicator label="Usuários SGU" value={sguRate} accent={KPI.secondary} darkMode={darkMode} />
          </div>
        </Col>
        <UsersChartCard
          title="Registros por Mês"
          items={distributions.registrationsByMonth}
          colorKey="green"
          darkMode={darkMode}
          lg={8}
          height={320}
        />
      </Row>

      <Row className="g-4">
        <UsersChartCard title="Usuários por País" items={distributions.byCountry} colorKey="teal" darkMode={darkMode} lg={6} height={280} />
        <UsersChartCard title="Usuários por Empresa" items={distributions.byCompany} colorKey="purple" darkMode={darkMode} lg={6} height={280} />
        <UsersChartCard title="Usuários por Aplicativo" items={distributions.byApp} colorKey="blue" darkMode={darkMode} lg={6} height={280} />
        <UsersChartCard title="Usuários por Sistema" items={distributions.bySystem} colorKey="lime" darkMode={darkMode} lg={6} height={280} />
      </Row>
    </>
  );
};

export default UsersDashboard;
